/**
 * One game, picked from a short list (screens 03, 09): Today, This week, or a
 * day the fan picks. The list is the server's own preview for those days, so
 * a started game arrives already marked and can never be chosen.
 */
import { useSquaresPreviewGames } from "@sportsmet/contest-sdk/private";
import { Radio, SegmentedControl, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useMemo, useState } from "react";

import { shiftDay, todayInZone } from "../scores/calendarDay";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  SQUARES_ONE_GAME_EMPTY,
  SQUARES_ONE_GAME_LIST_LABEL,
  SQUARES_ONE_GAME_PICK_DATE,
  SQUARES_ONE_GAME_THIS_WEEK,
  SQUARES_ONE_GAME_TODAY,
  SQUARES_PREVIEW_LOADING,
  SQUARES_PREVIEW_STARTED,
} from "./squaresCopy";
import { SquaresGameDayCalendar } from "./SquaresGameDayCalendar";
import { squaresKickoffLabel, squaresMatchupLabel } from "./squaresModel";
import type { typeSquaresGridType } from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

type typeDayMode = "today" | "week" | "date";

const DAY_MODES = [
  { value: "today", label: SQUARES_ONE_GAME_TODAY },
  { value: "week", label: SQUARES_ONE_GAME_THIS_WEEK },
  { value: "date", label: SQUARES_ONE_GAME_PICK_DATE },
] as const;

export type typeSquaresOneGamePickerProps = {
  leagueKey: string;
  gridType: typeSquaresGridType;
  selectedEventId: string | null;
  onSelect: (eventId: string) => void;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
  /** Events that already have a grid, left out of the list (Add a grid). */
  hiddenEventIds?: readonly string[] | undefined;
};

export function squaresDayRange(input: {
  mode: typeDayMode;
  today: string;
  pickedDay: string;
}): { fromDay: string; toDay: string } {
  if (input.mode === "today")
    return { fromDay: input.today, toDay: input.today };
  if (input.mode === "week") {
    return {
      fromDay: input.today,
      toDay: shiftDay({ day: input.today, deltaDays: 6 }),
    };
  }
  return { fromDay: input.pickedDay, toDay: input.pickedDay };
}

export const SquaresOneGamePicker = (
  props: typeSquaresOneGamePickerProps,
): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const today = useMemo(
    () => todayInZone({ nowMs: Date.now(), timeZone }),
    [timeZone],
  );
  const [mode, setMode] = useState<typeDayMode>("today");
  const [pickedDay, setPickedDay] = useState<string>(today);
  const range = squaresDayRange({ mode, today, pickedDay });

  const preview = useSquaresPreviewGames(
    {
      leagueKey: props.leagueKey,
      gridType: props.gridType,
      gameRule: {
        presetKey: "customDates",
        customDates: { mode: "range", ...range },
        excludedEventIds: [],
      },
    },
    { enabled: range.fromDay !== "" },
  );

  const hidden = new Set(props.hiddenEventIds ?? []);
  const games = (preview.data?.data?.games ?? []).filter(
    (game) => !hidden.has(game.eventId),
  );

  return (
    <div className="flex flex-col gap-3" data-testid="squares-one-game">
      <SegmentedControl
        label={SQUARES_ONE_GAME_LIST_LABEL}
        options={DAY_MODES}
        value={mode}
        fullWidth
        testId="squares-one-game-days"
        onValueChange={(value) => {
          setMode(value as typeDayMode);
        }}
      />
      {mode === "date" ? (
        <SquaresGameDayCalendar
          leagueKey={props.leagueKey}
          todayDay={today}
          selectedDay={pickedDay}
          timeZone={timeZone}
          onSelectDay={setPickedDay}
        />
      ) : null}
      {preview.isLoading ? (
        <div
          className="flex flex-col gap-2"
          aria-label={SQUARES_PREVIEW_LOADING}
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : games.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="squares-one-game-empty"
        >
          {SQUARES_ONE_GAME_EMPTY}
        </p>
      ) : (
        <Radio
          label={SQUARES_ONE_GAME_LIST_LABEL}
          labelHidden
          value={props.selectedEventId ?? undefined}
          onValueChange={props.onSelect}
          items={games.map((game) => {
            const kickoff = squaresKickoffLabel({
              kickoffAt: game.kickoffAt,
              timeZone,
            });
            return {
              value: game.eventId,
              label: squaresMatchupLabel({
                awayTeamId: game.awayTeamId,
                homeTeamId: game.homeTeamId,
                teamsById: props.teamsById,
              }),
              description: game.isStarted
                ? `${kickoff} · ${SQUARES_PREVIEW_STARTED}`
                : kickoff,
              disabled: game.isStarted,
            };
          })}
        />
      )}
    </div>
  );
};
